<template>
  <div class="rich-text-editor" :class="{ 'rich-text-editor-invalid': invalid }">
    <div class="rich-text-toolbar" aria-label="Text formatting">
      <button
        v-for="action in actions"
        :key="action.command"
        class="rich-text-button"
        :class="{ 'rich-text-button-active': activeCommands[action.command] }"
        type="button"
        :aria-label="action.label"
        :aria-pressed="activeCommands[action.command]"
        @mousedown.prevent
        @click="runCommand(action.command)"
      >
        <i :class="action.icon" aria-hidden="true" />
      </button>

      <span class="rich-text-divider" aria-hidden="true" />

      <div class="rich-text-emoji-wrap">
        <button
          class="rich-text-button"
          type="button"
          aria-label="Choose emoji"
          :aria-expanded="emojiPickerOpen"
          @mousedown.prevent
          @click="emojiPickerOpen = !emojiPickerOpen"
        >
          <i class="ri-emotion-line" aria-hidden="true" />
        </button>
        <div v-if="emojiPickerOpen" class="rich-text-emoji-popover">
          <emoji-picker class="rich-text-emoji-picker" @emoji-click="insertEmoji" />
        </div>
      </div>
    </div>

    <div class="rich-text-body">
      <div
        v-if="isEmpty"
        class="rich-text-placeholder"
        aria-hidden="true"
      >
        {{ placeholder }}
      </div>
      <div
        ref="editor"
        class="rich-text-input"
        contenteditable="true"
        role="textbox"
        aria-multiline="true"
        :aria-label="placeholder"
        @blur="handleBlur"
        @input="syncFromEditor"
        @keyup="refreshActiveCommands"
        @mouseup="refreshActiveCommands"
      />
    </div>
    <div v-if="maxLength > 0" class="rich-text-counter" aria-live="polite">
      {{ characterCount }} / {{ maxLength }}
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import 'emoji-picker-element'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Write something...' },
  invalid: { type: Boolean, default: false },
  maxLength: { type: Number, default: 0 }
})

const emit = defineEmits(['update:modelValue', 'blur'])

const editor = ref(null)
const emojiPickerOpen = ref(false)
const isEmpty = ref(true)
const characterCount = ref(0)
const lastEmittedHtml = ref(null)
const activeCommands = reactive({
  bold: false,
  italic: false,
  underline: false,
  insertUnorderedList: false,
  insertOrderedList: false
})

const actions = [
  { command: 'bold', label: 'Bold', icon: 'ri-bold' },
  { command: 'italic', label: 'Italic', icon: 'ri-italic' },
  { command: 'underline', label: 'Underline', icon: 'ri-underline' },
  { command: 'insertUnorderedList', label: 'Bulleted list', icon: 'ri-list-unordered' },
  { command: 'insertOrderedList', label: 'Numbered list', icon: 'ri-list-ordered' }
]

const decodeHtmlEntities = (value = '') => {
  let decoded = String(value || '')
  if (!decoded) return ''

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    const textarea = document.createElement('textarea')
    textarea.innerHTML = decoded
    decoded = textarea.value
    if (decoded === previous) break
  }

  return decoded
}

const hasSupportedHtmlMarkup = (value = '') => /<\/?(p|div|br|strong|b|em|i|u|ul|ol|li)(\s|>|\/)/i.test(String(value || ''))

const escapeHtml = (value = '') => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const plainTextToHtml = (value = '') => String(value || '')
  .split(/\n{2,}/)
  .map((block) => {
    const lines = block.split(/\n/)
    return `<p>${lines.map((line) => escapeHtml(line)).join('<br>')}</p>`
  })
  .join('')

const normalizeEditorInput = (value = '') => {
  const source = String(value || '').trim()
  if (!source) return ''
  if (hasSupportedHtmlMarkup(source)) return source
  const decoded = decodeHtmlEntities(source).trim()
  return hasSupportedHtmlMarkup(decoded) ? decoded : plainTextToHtml(decoded)
}

const sanitizeHtml = (value = '') => {
  const root = document.createElement('div')
  root.innerHTML = normalizeEditorInput(value)

  root.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove())
  root.querySelectorAll('*').forEach((node) => {
    const tag = node.tagName.toLowerCase()
    const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'p', 'div', 'br', 'ul', 'ol', 'li']

    if (!allowedTags.includes(tag)) {
      node.replaceWith(document.createTextNode(node.textContent || ''))
      return
    }

    Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name))
  })

  return root.innerHTML.trim()
}

const truncateHtml = (value = '', limit = 0) => {
  if (!limit) return value
  const root = document.createElement('div')
  root.innerHTML = value
  let remaining = limit

  const trimNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const characters = Array.from(node.textContent || '')
      if (characters.length > remaining) node.textContent = characters.slice(0, remaining).join('')
      remaining = Math.max(0, remaining - characters.length)
      return
    }

    Array.from(node.childNodes).forEach((child) => {
      if (remaining <= 0) child.remove()
      else trimNode(child)
    })
  }

  trimNode(root)
  return root.innerHTML.trim()
}

const applyCharacterLimit = (value = '') => truncateHtml(sanitizeHtml(value), Math.max(0, Number(props.maxLength) || 0))
const updateCharacterCount = () => {
  characterCount.value = Array.from(editor.value?.textContent?.replace(/\u00a0/g, ' ') || '').length
}

const placeCaretAtEnd = () => {
  if (!editor.value || !isEditorActive()) return
  const range = document.createRange()
  range.selectNodeContents(editor.value)
  range.collapse(false)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

const setEditorHtml = (value = '') => {
  if (!editor.value) return
  const nextHtml = applyCharacterLimit(value)
  if (editor.value.innerHTML !== nextHtml) {
    editor.value.innerHTML = nextHtml
  }
  isEmpty.value = !editor.value.textContent?.replace(/\u00a0/g, ' ').trim()
  updateCharacterCount()
}

const isEditorActive = () => editor.value && document.activeElement === editor.value

const syncFromEditor = () => {
  if (!editor.value) return
  const originalHtml = sanitizeHtml(editor.value.innerHTML)
  const sanitized = applyCharacterLimit(originalHtml)
  if (sanitized !== originalHtml) {
    editor.value.innerHTML = sanitized
    placeCaretAtEnd()
  }
  isEmpty.value = !editor.value.textContent?.replace(/\u00a0/g, ' ').trim()
  updateCharacterCount()
  if (isEmpty.value) {
    editor.value.innerHTML = ''
    lastEmittedHtml.value = ''
    emit('update:modelValue', '')
  } else {
    lastEmittedHtml.value = sanitized
    emit('update:modelValue', sanitized)
  }
  refreshActiveCommands()
}

const focusEditor = () => {
  editor.value?.focus()
}

const runCommand = (command) => {
  focusEditor()
  document.execCommand(command, false, null)
  syncFromEditor()
}

const insertEmoji = (event) => {
  const emoji = event.detail?.unicode || event.detail?.emoji?.unicode || ''
  if (!emoji) return
  focusEditor()
  document.execCommand('insertText', false, emoji)
  emojiPickerOpen.value = false
  syncFromEditor()
}

const refreshActiveCommands = () => {
  Object.keys(activeCommands).forEach((command) => {
    activeCommands[command] = document.queryCommandState(command)
  })
}

const handleBlur = () => {
  syncFromEditor()
  emit('blur')
}

onMounted(() => {
  setEditorHtml(props.modelValue)
})

watch(() => props.modelValue, (value) => {
  const nextHtml = applyCharacterLimit(value)
  if (isEditorActive() && nextHtml === lastEmittedHtml.value) {
    isEmpty.value = !editor.value?.textContent?.replace(/\u00a0/g, ' ').trim()
    return
  }
  setEditorHtml(value)
})

watch(() => props.maxLength, () => setEditorHtml(props.modelValue))
</script>

<style scoped>
.rich-text-editor {
  overflow: visible;
}

.rich-text-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  border-bottom: 1px solid rgb(226 232 240);
  padding-bottom: 0.85rem;
  margin-bottom: 1rem;
}

.rich-text-button {
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.75rem;
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-size: 1.05rem;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}

.rich-text-button:hover,
.rich-text-button-active {
  border-color: rgb(191 219 254);
  background: rgb(239 246 255);
  color: rgb(37 99 235);
}

.rich-text-button:hover {
  transform: translateY(-1px);
}

.rich-text-divider {
  height: 1.75rem;
  width: 1px;
  background: rgb(226 232 240);
  margin: 0 0.15rem;
}

.rich-text-emoji-wrap {
  position: relative;
}

.rich-text-emoji-popover {
  position: absolute;
  right: 0;
  top: calc(100% + 0.75rem);
  z-index: 40;
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: white;
  box-shadow: 0 24px 55px rgb(15 23 42 / 0.18);
}

:deep(.rich-text-emoji-picker) {
  width: min(21rem, calc(100vw - 2rem));
  height: 24rem;
  --border-radius: 1rem;
  --outline-color: rgb(37 99 235);
}

.rich-text-body {
  position: relative;
}

.rich-text-placeholder {
  pointer-events: none;
  position: absolute;
  left: 0;
  top: 0;
  color: rgb(156 163 175);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.6;
}

.rich-text-input {
  min-height: 11rem;
  width: 100%;
  outline: none;
  color: rgb(15 23 42);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.rich-text-counter {
  margin-top: 0.65rem;
  text-align: right;
  color: rgb(100 116 139);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
}

.rich-text-input :deep(ul),
.rich-text-input :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.75rem;
  list-style-position: outside;
}

.rich-text-input :deep(ul) {
  list-style-type: disc;
}

.rich-text-input :deep(ol) {
  list-style-type: decimal;
}

.rich-text-input :deep(li) {
  display: list-item;
  padding-left: 0.2rem;
}

.rich-text-input :deep(li + li) {
  margin-top: 0.2rem;
}

.rich-text-input :deep(ul ul) {
  list-style-type: circle;
}

.rich-text-input :deep(ol ol) {
  list-style-type: lower-alpha;
}

.rich-text-input :deep(p),
.rich-text-input :deep(div) {
  margin: 0 0 0.5rem;
}

.rich-text-editor-invalid .rich-text-toolbar {
  border-color: rgb(254 202 202);
}
</style>
